import {FormattedPageTitle} from "@/components/pages";
import RelatedDataResolver, {ReferenceType, RelatedDataTargetType} from "@/components/RelatedDataResolver";
import {commonTranslations} from "@/utils/translations";
import {Box} from "@mui/material";
import {FC, ReactNode, useCallback, useMemo, useState} from "react";
import {
    DeleteIvrMutationVariables,
    IvrRetargetsQuery,
    useDeleteIvrMutation,
    useIvrRetargetsQuery,
} from "./queries.generated";
import {useForm} from "@/utils/Form";
import {RelatedDataQuery, useRelatedDataQuery} from "@/utils/queries.generated";
import {NumberType} from "@/api";
import {getTargetsForReferenceType, processRelatedDataQueryResult} from "@/utils/relatedDataTools";
import {useIvrNumberIdQuery} from "../../queries.generated";
import {FormActionButtons, LoadingIcon} from "@dvrt/graphql-widgets";
import Section from "@/components/Section";
import {defineMessages} from "react-intl";
import {showNotification} from "@/actions";
import {useNavigator} from "@/hooks";

type DataSource = {
    loading: boolean;

    domainId: number;
    ivrId: number;
    ivrNumberId: number;

    relatedData: RelatedDataQuery["relatedData"];
    targets: IvrRetargetsQuery;
};

const translations = defineMessages({
    title: {id: "delete_ivr.title", defaultMessage: "Delete auto attendant"},
    relatedData: {
        id: "delete_ivr.related_data",
        defaultMessage:
            "This auto attendant is referenced by other items. To delete it, the following references must be resolved:",
    },
    deletedNotification: {
        id: "delete_ivr.deleted_notification",
        defaultMessage: "Auto attendant has been successfully deleted.",
    },
});

const useDataSource = (domainId: number, ivrId: number): DataSource => {
    const numberIdQuery = useIvrNumberIdQuery({variables: {ivrId}, fetchPolicy: "no-cache"});

    const relatedDataQuery = useRelatedDataQuery({
        variables: {targetId: ivrId, numberType: NumberType.Ivr},
        fetchPolicy: "no-cache",
    });

    const targetsQuery = useIvrRetargetsQuery({
        variables: {domainId},
        fetchPolicy: "no-cache",
    });

    return {
        loading: relatedDataQuery.loading || targetsQuery.loading || numberIdQuery.loading,
        domainId,
        ivrId,
        ivrNumberId: numberIdQuery.data?.ivrs.items[0]?.number.id,
        relatedData: relatedDataQuery.data?.relatedData,
        targets: targetsQuery.data,
    };
};

type RelatedDataIvr = DeleteIvrMutationVariables["relatedData"];

type IvrDeletePageProps = {
    domainId: number;
    ivrId: number;
};

type FormContentProps = {
    dataSource: DataSource;
    onSuccess: () => void;
};

const FormContent: FC<FormContentProps> = ({dataSource, onSuccess}) => {
    const [deleting, setDeleting] = useState(false);
    const form = useForm(
        useDeleteIvrMutation,
        "deleteIvr",
        {
            id: dataSource.ivrId,
            relatedData: {
                gateways: [],
                ivrActions: [],
                overrideCaller: [],
            },
        },
        (_) => showNotification(translations.deletedNotification, "success"),
    );

    const processedReferences = useMemo(
        () => processRelatedDataQueryResult(dataSource.relatedData, RelatedDataTargetType.IVR, dataSource.ivrNumberId),
        [dataSource.relatedData, dataSource.ivrNumberId],
    );

    const convertedTargets = useMemo(
        () => ({
            ivrActions: getTargetsForReferenceType(ReferenceType.IVR_ACTION, dataSource.targets),
            gateways: getTargetsForReferenceType(ReferenceType.GATEWAY, dataSource.targets),
            overrideCallers: getTargetsForReferenceType(ReferenceType.OVERRIDE_CALLER, dataSource.targets),
        }),
        [dataSource.targets],
    );

    const hasRelatedData =
        dataSource.relatedData.ivrActions.length > 0 ||
        dataSource.relatedData.gateways.length > 0 ||
        dataSource.relatedData.overrideCaller.length > 0;

    const relatedDataIsResolved = useMemo(() => {
        for (const referenceKey of Object.keys(processedReferences)) {
            const references = processedReferences[referenceKey as keyof typeof processedReferences];
            const formValues = form.values.relatedData[referenceKey as keyof RelatedDataIvr];

            // since the initial data starts with empty arrays, and as the user selects items,
            // the length of both arrays should always match if all references are resolved
            if (references.length !== formValues.length) {
                return false;
            }
        }

        return true;
    }, [form.values.relatedData, processedReferences]);

    const handleSubmit = useCallback(async () => {
        setDeleting(true);
        if (await form.submit()) {
            onSuccess();
        }
        setDeleting(false);
    }, [form, onSuccess]);

    return (
        <Box display="flex" flexDirection="column">
            {hasRelatedData && (
                <Section label={translations.relatedData}>
                    <RelatedDataResolver<RelatedDataTargetType.IVR, RelatedDataIvr>
                        domainId={dataSource.domainId}
                        onChange={(relatedData) => form.updateValues({relatedData})}
                        value={form.values.relatedData}
                        errorHelper={form.errorHelper}
                        ivrActions={{
                            references: processedReferences.ivrActions,
                            targets: convertedTargets.ivrActions,
                        }}
                        overrideCaller={{
                            references: processedReferences.overrideCaller,
                            targets: convertedTargets.overrideCallers,
                        }}
                        gateways={{
                            references: processedReferences.gateways,
                            targets: convertedTargets.gateways,
                        }}
                    />
                </Section>
            )}
            <Box>
                <FormActionButtons
                    mainAction={handleSubmit}
                    mainButtonText={commonTranslations.delete}
                    mainButtonColor="secondary"
                    mainButtonEnabled={!hasRelatedData || (relatedDataIsResolved && form.hasChanged && !deleting)}
                    secondaryAction={() => form.reset()}
                    secondaryButtonEnabled={form.hasChanged && !deleting}
                    secondaryButtonHidden={!hasRelatedData}
                    pending={deleting}
                />
            </Box>
        </Box>
    );
};

const LoadingWrapper = ({loading, children}: {loading: boolean; children: ReactNode}) => {
    if (loading) {
        return <LoadingIcon />;
    }
    return children;
};

const IvrDeletePage: FC<IvrDeletePageProps> = ({domainId, ivrId}) => {
    const navigator = useNavigator();
    const dataSource = useDataSource(domainId, ivrId);

    const onSuccess = useCallback(() => {
        navigator.popSegment(2);
    }, [navigator]);

    return (
        <Box sx={{marginBottom: 20}}>
            <FormattedPageTitle desc={translations.title} />
            <LoadingWrapper loading={dataSource.loading}>
                <FormContent dataSource={dataSource} onSuccess={onSuccess} />
            </LoadingWrapper>
        </Box>
    );
};

export default IvrDeletePage;
